import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DatabaseBackup,
  HardDriveDownload,
  RotateCcw,
  Loader2,
  CheckCircle2,
  FolderOpen,
  Clock,
  Play,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatSize(bytes) {
  const n = Number(bytes);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Backup() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState("02:00");
  const [folder, setFolder] = useState({ id: null, name: null });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/backup/status");
      setStatus(data);
      const s = data.settings || {};
      setEnabled(!!s.enabled);
      if (s.time) setTime(s.time);
      setFolder({ id: data.folder_id || null, name: data.folder_name || null });
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const { data } = await api.get("/backup/files");
      setFiles(data.files || []);
    } catch (e) {
      // silent — likely not connected yet
    } finally {
      setFilesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    loadStatus();
  }, [isAdmin, loadStatus]);

  useEffect(() => {
    if (status?.drive_authorized) loadFiles();
  }, [status?.drive_authorized, loadFiles]);

  // Handle return from Google OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "connected") {
      toast.success("Google Drive collegato");
      window.history.replaceState({}, "", "/backup");
      loadStatus();
    } else if (params.get("gcal") === "error") {
      toast.error("Collegamento Google non riuscito");
      window.history.replaceState({}, "", "/backup");
    }
  }, [loadStatus]);

  const connectDrive = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/login", { params: { next: "/backup" } });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error("Impossibile avviare il collegamento a Google");
    }
  };

  const disconnectDrive = async () => {
    if (!confirm("Scollegare Google Drive? Il backup automatico non verrà più eseguito finché non ricolleghi l'account.")) return;
    try {
      await api.post("/backup/disconnect");
      toast.success("Google Drive scollegato");
      setFiles([]);
      loadStatus();
    } catch (e) {
      toast.error("Impossibile scollegare Google Drive");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.post("/backup/settings", { enabled, time });
      toast.success("Impostazioni backup salvate");
      loadStatus();
    } catch (e) {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/backup/run");
      toast.info("Backup avviato… attendi qualche istante");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get("/backup/status");
          setStatus(data);
          const s = data.settings || {};
          if (!s.backup_running) {
            setRunning(false);
            if (s.last_error) toast.error("Backup non riuscito: " + s.last_error);
            else toast.success("Backup salvato su Google Drive");
            loadFiles();
            return;
          }
        } catch (_) {
          // keep polling on transient errors
        }
        if (tries > 80) {
          setRunning(false);
          toast.message("Backup ancora in corso: ricontrolla tra poco gli ultimi backup.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup non avviato");
      setRunning(false);
    }
  };

  const doRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      await api.post("/backup/restore", { file_id: restoreTarget.id });
      toast.success("Backup ripristinato. Ricarico l'app…");
      setRestoreTarget(null);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ripristino non riuscito");
      setRestoring(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="tp-glass rounded-3xl p-10 text-center max-w-lg mx-auto mt-10" data-testid="backup-no-access">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
          <ShieldAlert className="text-amber-400" />
        </div>
        <h3 className="text-xl font-semibold text-white">Accesso riservato</h3>
        <p className="text-sm text-zinc-400 mt-1">Solo gli amministratori possono gestire i backup.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Caricamento…
      </div>
    );
  }

  const driveOk = status?.drive_authorized;
  const lastBackup = status?.settings?.last_backup_at;

  return (
    <div className="space-y-6 tp-fade-in max-w-3xl" data-testid="backup-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sicurezza dati</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-1 flex items-center gap-3">
          <DatabaseBackup className="text-fuchsia-400" /> Backup
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          Programma un backup giornaliero di tutti i dati su Google Drive e ripristinalo quando vuoi.
        </p>
      </div>

      {/* Connection */}
      {!driveOk ? (
        <div className="tp-glass rounded-3xl p-6" data-testid="backup-connect-card">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-2xl bg-white/5 flex items-center justify-center shrink-0">
              <HardDriveDownload className="text-zinc-300" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-white">Collega Google Drive</h3>
              <p className="text-sm text-zinc-400 mt-1">
                Serve l'accesso a Google Drive per salvare e ripristinare i backup. Verrà usato lo stesso
                account Google.
              </p>
              <Button
                onClick={connectDrive}
                data-testid="backup-connect-button"
                className="mt-4 h-10 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white hover:opacity-90"
              >
                Collega Google Drive
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="tp-glass rounded-3xl p-5 flex items-center gap-3" data-testid="backup-connected-card">
          <CheckCircle2 className="text-emerald-400" size={20} />
          <span className="text-sm text-zinc-200">Google Drive collegato</span>
          <Button
            onClick={disconnectDrive}
            data-testid="backup-disconnect-button"
            variant="ghost"
            className="ml-auto h-9 rounded-full text-zinc-400 hover:text-red-300 hover:bg-red-500/10 border border-white/10"
          >
            Scollega
          </Button>
        </div>
      )}

      {/* Schedule settings */}
      <div className={`tp-glass rounded-3xl p-6 space-y-5 ${!driveOk ? "opacity-50 pointer-events-none" : ""}`} data-testid="backup-settings-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-white">Backup automatico giornaliero</h3>
            <p className="text-sm text-zinc-400">Esegue un backup ogni giorno all'orario scelto.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="backup-enabled-switch" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 flex items-center gap-1.5"><Clock size={13} /> Orario (Italia)</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="backup-time-input"
              className="bg-black/30 border-white/10 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 flex items-center gap-1.5"><FolderOpen size={13} /> Cartella Google Drive (fissa)</Label>
            <div
              data-testid="backup-folder-fixed"
              className="w-full h-10 rounded-md bg-black/30 border border-white/10 px-3 text-sm text-white truncate flex items-center gap-2"
              title={folder.id || ""}
            >
              <FolderOpen size={14} className="text-fuchsia-300 shrink-0" />
              <span className="truncate">{folder.name || "Cartella di backup predefinita"}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button
            onClick={saveSettings}
            disabled={saving}
            data-testid="backup-save-button"
            className="h-10 rounded-full bg-white text-black hover:bg-zinc-200"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={15} /> : null}
            Salva impostazioni
          </Button>
          <Button
            onClick={runNow}
            disabled={running}
            data-testid="backup-run-now-button"
            variant="ghost"
            className="h-10 rounded-full text-fuchsia-300 hover:bg-fuchsia-500/10 border border-fuchsia-500/30"
          >
            {running ? <Loader2 className="animate-spin mr-2" size={15} /> : <Play size={15} className="mr-1.5" />}
            {running ? "Backup in corso…" : "Esegui backup ora"}
          </Button>
          {lastBackup && (
            <span className="text-xs text-zinc-500" data-testid="backup-last-info">
              Ultimo backup: {formatWhen(lastBackup)}
            </span>
          )}
        </div>
      </div>

      {/* Backups list / restore */}
      {driveOk && (
        <div className="tp-glass rounded-3xl p-6" data-testid="backup-list-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white">Backup disponibili</h3>
            <Button variant="ghost" size="sm" onClick={loadFiles} className="text-zinc-400 hover:text-white text-xs">
              Aggiorna
            </Button>
          </div>
          {filesLoading ? (
            <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Caricamento…</div>
          ) : files.length === 0 ? (
            <p className="text-sm text-zinc-500">Nessun backup trovato nella cartella selezionata.</p>
          ) : (
            <div className="space-y-2">
              {files.map((f) => (
                <div
                  key={f.id}
                  data-testid={`backup-file-${f.id}`}
                  className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3"
                >
                  <DatabaseBackup size={16} className="text-zinc-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{f.name}</div>
                    <div className="text-[11px] text-zinc-500">
                      {formatWhen(f.createdTime)} {f.size ? `· ${formatSize(f.size)}` : ""}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRestoreTarget(f)}
                    data-testid={`backup-restore-${f.id}`}
                    className="text-xs text-amber-300 hover:bg-amber-500/10 border border-amber-500/30 rounded-full"
                  >
                    <RotateCcw size={13} className="mr-1.5" /> Ripristina
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Restore confirm dialog */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent className="tp-glass-strong border-white/10 text-white" data-testid="backup-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="text-amber-400" size={18} /> Ripristinare questo backup?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-300">
            Tutti i dati attuali verranno <span className="text-amber-300 font-medium">sostituiti</span> con quelli del
            backup{" "}
            <span className="text-white font-medium">{restoreTarget?.name}</span> del {formatWhen(restoreTarget?.createdTime)}.
            L'operazione non è reversibile.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRestoreTarget(null)} disabled={restoring} className="text-zinc-300">
              Annulla
            </Button>
            <Button
              onClick={doRestore}
              disabled={restoring}
              data-testid="backup-restore-confirm"
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {restoring ? <Loader2 className="animate-spin mr-2" size={15} /> : <RotateCcw size={15} className="mr-1.5" />}
              Sostituisci e ripristina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
